import { useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { ColorSwatches } from '@/components/ColorSwatches';
import { GlossSelector } from '@/components/GlossSelector';
import { EmailCaptureModal } from '@/components/EmailCaptureModal';
import { usePreviewJob } from '@/hooks/usePreviewJob';
import { isEmbedded } from '@/utils/config';

const IS_EMBED = isEmbedded();

export function ConfigureScreen() {
  const uploadedImage = useVisualizerStore((s) => s.uploadedImage);
  const selectedFinish = useVisualizerStore((s) => s.selectedFinish);
  const refinishSurround = useVisualizerStore((s) => s.refinishSurround);
  const userEmail = useVisualizerStore((s) => s.userEmail);
  const setUserEmail = useVisualizerStore((s) => s.setUserEmail);
  const setAppState = useVisualizerStore((s) => s.setAppState);
  const setRefinishSurround = useVisualizerStore((s) => s.setRefinishSurround);
  const reset = useVisualizerStore((s) => s.reset);
  const { submit: submitPreviewJob, isSubmitting } = usePreviewJob();
  const [showEmailModal, setShowEmailModal] = useState(false);

  const runGenerate = async () => {
    if (!uploadedImage) return;
    setAppState('MODEL_LOADING');
    try {
      await submitPreviewJob();
    } catch {
      // Error is handled by usePreviewJob internally (sets error state)
    }
  };

  const handleGenerate = async () => {
    if (!uploadedImage) return;
    if (!userEmail) {
      setShowEmailModal(true);
      return;
    }
    await runGenerate();
  };

  const handleEmailSubmit = async (email: string) => {
    setUserEmail(email);
    setShowEmailModal(false);
    await runGenerate();
  };

  return (
    <div className={`flex flex-col bg-gradient-radial-blue ${IS_EMBED ? 'min-h-full' : 'h-full overflow-y-auto'}`}>
      {/* Image preview */}
      <div className="relative mx-auto w-full max-w-2xl px-4 pt-4 animate-slide-up">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/60 bg-slate-100 shadow-lg">
          {uploadedImage && (
            <img
              src={uploadedImage.sourceUrl}
              alt="Your bathroom"
              className="h-auto w-full object-contain transition-opacity duration-300"
              onLoad={(e) => (e.currentTarget.style.opacity = '1')}
              style={{ opacity: 0 }}
            />
          )}
          {!uploadedImage && <div className="skeleton aspect-video w-full" />}
        </div>
      </div>

      {/* Configuration panel */}
      <div className="mx-auto w-full max-w-2xl flex-1 px-4 py-3 animate-slide-up" style={{ animationDelay: '80ms' }}>
        {/* Selected finish pill */}
        <div className="mb-3 flex items-center gap-2">
          <div
            className="h-5 w-5 rounded-full border border-slate-200 shadow-sm"
            style={{ backgroundColor: selectedFinish.color }}
          />
          <span className="text-sm font-semibold text-slate-700">
            {selectedFinish.name}
          </span>
          <span className="text-xs text-slate-400 font-mono">
            {selectedFinish.color}
          </span>
        </div>

        {/* Finish selector */}
        <div className="mb-3">
          <h3 className="mb-1.5 text-xs font-bold text-slate-900 tracking-wide uppercase">
            Choose Your Finish
          </h3>
          <ColorSwatches />
        </div>

        {/* Gloss selector */}
        <div className="mb-4">
          <h3 className="mb-1.5 text-xs font-bold text-slate-900 tracking-wide uppercase">
            Choose Your Gloss
          </h3>
          <GlossSelector />
        </div>

        {/* Surround selector */}
        <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm transition-colors hover:border-bss-blue/40">
          <input
            type="checkbox"
            checked={refinishSurround}
            onChange={(e) => setRefinishSurround(e.currentTarget.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-bss-blue focus:ring-bss-blue"
          />
          <span>
            <span className="block text-sm font-semibold text-slate-800">
              Also refinish shower/tub surround
            </span>
            <span className="mt-0.5 block text-xs leading-5 text-slate-500">
              Select this if the visible shower walls or tub surround should match the new finish.
            </span>
          </span>
        </label>

        {/* CTA */}
        <button
          onClick={handleGenerate}
          disabled={isSubmitting}
          className="group relative w-full overflow-hidden rounded-2xl bg-bss-blue py-3 text-base font-bold text-white shadow-lg shadow-blue-600/30 transition-all duration-200 hover:bg-bss-blue-dark hover:shadow-blue-600/40 hover:-translate-y-1 hover:scale-[1.01] active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {isSubmitting ? (
              <>
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Preparing...
              </>
            ) : (
              <>
                <svg className="h-5 w-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.375 9.75a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                Generate Preview
              </>
            )}
          </span>
        </button>

        {/* Start over */}
        <button
          onClick={reset}
          className="mt-2 w-full py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          Start Over
        </button>
      </div>

      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailSubmit}
      />
    </div>
  );
}
